import { useEffect, useRef, useState, type HTMLAttributes } from 'react'
import { useI18n } from '../../features/i18n/useI18n'

type LanguageSwitcherProps = HTMLAttributes<HTMLDivElement>

export function LanguageSwitcher({ className = '', ...props }: LanguageSwitcherProps) {
  const { locale, setLocale } = useI18n()
  const [open, setOpen] = useState(false)
  const controlRef = useRef<HTMLDivElement>(null)
  const isPublic = className.split(/\s+/).includes('public-language-switcher')

  useEffect(() => {
    if (!isPublic || !open) return
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!controlRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutsidePress)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePress)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [isPublic, open])

  function choose(next: 'en' | 'es') {
    setLocale(next)
    setOpen(false)
  }

  const choices = (
    <div className="language-switcher" role="group" aria-label={locale === 'es' ? 'Seleccionar idioma' : 'Choose language'} {...props}>
      <button type="button" className={locale === 'en' ? 'active' : ''} aria-pressed={locale === 'en'} onClick={() => choose('en')}>
        <span className="language-flag" aria-hidden="true">🇺🇸</span>
        <span>English</span>
        {locale === 'en' && <span className="language-check" aria-hidden="true">✓</span>}
      </button>
      <button type="button" className={locale === 'es' ? 'active' : ''} aria-pressed={locale === 'es'} onClick={() => choose('es')}>
        <span className="language-flag" aria-hidden="true">🇪🇸</span>
        <span>Español</span>
        {locale === 'es' && <span className="language-check" aria-hidden="true">✓</span>}
      </button>
    </div>
  )

  if (!isPublic) return <div className={className}>{choices}</div>

  return (
    <div ref={controlRef} className={className}>
      <button
        type="button"
        className="public-language-trigger"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={locale === 'es' ? 'Cambiar idioma' : 'Change language'}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="language-flag" aria-hidden="true">{locale === 'es' ? '🇪🇸' : '🇺🇸'}</span>
        <span>{locale === 'es' ? 'Español' : 'English'}</span>
        <span aria-hidden="true">⌄</span>
      </button>
      {open && <div className="public-language-popover">{choices}</div>}
    </div>
  )
}
